import { getUser } from '../auth/authService';

// The Integration Hub lives in the Integrations microservice, alongside the
// Success Requests tracker. Same fetch + OIDC bearer pattern as
// successRequestService.js.
//
// Storage is one SQL row per (client, provider) for that pair's
// notes/links/schema, plus the nexus-integration-hub blob container for
// attachments. Reads and writes are always scoped by both ids — the UI is
// provider-first (see providerService.getProvidersOverview + the
// getProviderClients call below), but the underlying row is the same one a
// client-first view would edit.
const BASE = `${process.env.REACT_APP_INTEGRATION_MICROSERVICE_URL ?? ''}`;

async function authHeaders() {
    const user = await getUser();
    if (!user?.access_token) throw new Error('Not authenticated');
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.access_token}`,
    };
}

async function parseError(res) {
    try {
        const body = await res.json();
        return body?.detail ?? `HTTP ${res.status}`;
    } catch {
        const text = await res.text().catch(() => '');
        if (!text || text.trimStart().startsWith('<')) return `HTTP ${res.status}`;
        return text.slice(0, 300);
    }
}

async function request(path, options = {}) {
    const headers = { ...(await authHeaders()), ...(options.headers || {}) };
    const res = await fetch(`${BASE}${path}`, { ...options, headers });
    if (!res.ok) {
        const err = new Error(await parseError(res));
        err.status = res.status;
        throw err;
    }
    return res.json();
}

/** Best-effort display name for the signed-in user, for UpdatedBy attribution. */
export const currentUserName = async () => {
    const user = await getUser();
    return user?.profile?.name || user?.profile?.email || user?.profile?.sub || 'unknown';
};

/**
 * { document, providers } — client-level Notes-tab content (notesHtml,
 * links; general, not tied to any one provider), and the live provider list
 * merged with each provider's saved Schema-tab row. Used per-client from
 * inside a provider's page (the provider list on the response isn't used
 * from there). Contacts/Notes/Links/Files for a provider itself now live on
 * that provider's own row instead — see providerService.getProviderInfo.
 */
export const getIntegrationHub = (clientId) =>
    request(`/integration-hub/${encodeURIComponent(clientId)}`);

/** Save client-level Notes-tab content. 409s if `updatedOn` is stale. */
export const saveClientDoc = (clientId, { notesHtml, links }, updatedBy, updatedOn) =>
    request(`/integration-hub/${encodeURIComponent(clientId)}`, {
        method: 'PUT',
        body: JSON.stringify({
            notes_html: notesHtml || '', links: links || [],
            updated_by: updatedBy, updated_on: updatedOn ?? null,
        }),
    });

/** Save one client's Schema-tab content for this provider. 409s if `updatedOn` is stale. */
export const saveProviderDoc = (clientId, providerId, { schema }, updatedBy, updatedOn) =>
    request(`/integration-hub/${encodeURIComponent(clientId)}/providers/${encodeURIComponent(providerId)}`, {
        method: 'PUT',
        body: JSON.stringify({
            schema_data: schema || {},
            updated_by: updatedBy, updated_on: updatedOn ?? null,
        }),
    });

/**
 * Every client wired up to one provider, merged with each client's saved
 * notes/links/schema for it — the provider-first mirror of getIntegrationHub.
 */
export const getProviderClients = async (providerId) => {
    const data = await request(`/integration-providers/${encodeURIComponent(providerId)}/clients`);
    return data?.clients ?? [];
};

/** Remove a manually-added provider from this client's hub (soft delete). Never touches the shared provider dictionary. */
export const removeProviderDoc = (clientId, providerId, updatedBy) =>
    request(`/integration-hub/${encodeURIComponent(clientId)}/providers/${encodeURIComponent(providerId)}`, {
        method: 'DELETE',
        body: JSON.stringify({ updated_by: updatedBy }),
    });

/**
 * Live Data Source → Partition → Segment → file-title tree (names/structure
 * only — we don't read the run-history fields this endpoint also returns,
 * since management.NexusSegmentRun isn't a real run log; see SchemaTab.js).
 * Named for the backend route (/segment_runs/{client_id}, not ours to
 * rename) — kept because it's the only client-scoped call that returns this
 * whole tree in one request; the per-partition/per-data-source endpoints
 * would mean one call per data source instead. The API 404s a client with no
 * segments at all; that is an empty tree here, not an error.
 */
export const getSegmentTree = async (clientId) => {
    try {
        const data = await request(`/segment_runs/${encodeURIComponent(clientId)}`);
        return data?.data_sources ?? [];
    } catch (e) {
        if (e.status === 404) return [];
        throw e;
    }
};

/**
 * Datapoints (the file's column headers) for one segment: ActualName,
 * DisplayName, DataType, IsPrimaryKey, IsPII, Verified. Fetched per segment on
 * expand — a client can have hundreds and there is no bulk endpoint. The API
 * 404s a segment with no datapoints defined; that is an empty list, not an error.
 */
export const getSegmentDataPoints = async (segmentId) => {
    try {
        const data = await request(`/segments/${encodeURIComponent(segmentId)}/data-points`);
        return data?.data_points ?? [];
    } catch (e) {
        if (e.status === 404) return [];
        throw e;
    }
};

/**
 * Attachments are not stored anywhere but the blob container — this lists
 * whatever's actually there for a provider (filename/size/type come straight
 * from blob properties). Shared across every client that has this provider
 * (not per-client) — fetched on demand, same as segment data points, rather
 * than bundled into the provider-info GET.
 */
export const listProviderFiles = async (providerId) => {
    const data = await request(`/integration-providers/${encodeURIComponent(providerId)}/files`);
    return data?.attachments ?? [];
};

export const uploadProviderFiles = async (providerId, files, uploadedBy) => {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    form.append('uploaded_by', uploadedBy || '');

    const headers = await authHeaders();
    delete headers['Content-Type']; // let the browser set the multipart boundary
    const res = await fetch(
        `${BASE}/integration-providers/${encodeURIComponent(providerId)}/files`,
        { method: 'POST', headers, body: form },
    );
    if (!res.ok) throw new Error(await parseError(res));
    return (await res.json())?.attachments ?? [];
};

export const downloadProviderFileUrl = async (providerId, filename) => {
    const user = await getUser();
    const url = `${BASE}/integration-providers/${encodeURIComponent(providerId)}/files/${encodeURIComponent(filename)}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${user.access_token}` } });
    if (!res.ok) throw new Error(await parseError(res));
    return URL.createObjectURL(await res.blob());
};

export const deleteProviderFile = async (providerId, filename) => {
    const data = await request(
        `/integration-providers/${encodeURIComponent(providerId)}/files/${encodeURIComponent(filename)}`,
        { method: 'DELETE' },
    );
    return data?.attachments ?? [];
};
